import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Download, AccessTime } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import attendanceService from '../../services/attendanceService';
import storeService from '../../services/storeService';
import { Store } from '../../types';

const AttendanceExportPage: React.FC = () => {
  const { user } = useAuth();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    store_id: 'all',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    // 'inactive' exports deactivated employees (leavers / notice period) who
    // punched in during the range, so they can still be paid
    employee_status: 'active' as 'active' | 'inactive',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const response = await storeService.getStores({ limit: 1000 });
      setStores(response.stores || []);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to fetch stores',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportClick = (employeeStatus: 'active' | 'inactive' = 'active') => {
    const today = new Date().toISOString().split('T')[0];
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setExportFilters({
      store_id: 'all',
      start_date: employeeStatus === 'inactive' ? thirtyDaysAgo : today,
      end_date: today,
      employee_status: employeeStatus,
    });
    setExportDialogOpen(true);
  };

  const handleExport = async () => {
    try {
      setExporting(true);

      const params: any = {
        startDate: exportFilters.start_date,
        endDate: exportFilters.end_date,
        employeeStatus: exportFilters.employee_status,
      };

      if (exportFilters.store_id !== 'all') {
        params.storeId = exportFilters.store_id;
      }

      const blob = await attendanceService.exportAttendanceData(params);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const prefix = exportFilters.employee_status === 'inactive' ? 'inactive_attendance_export' : 'attendance_export';
      link.download = `${prefix}_${exportFilters.start_date}_to_${exportFilters.end_date}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'Attendance data exported successfully',
        severity: 'success',
      });
      setExportDialogOpen(false);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || err.message || 'Failed to export attendance data. Please try again.',
        severity: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <AccessTime sx={{ fontSize: 32, color: '#081122' }} />
        <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
          Attendance Export
        </Typography>
      </Box>

      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
          Export attendance data for analysis and reporting. Select the store and date range to generate the report.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => handleExportClick('active')}
            size="large"
            sx={{
              background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
              },
              px: 4,
              py: 1.5,
            }}
          >
            Export Attendance Data
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => handleExportClick('inactive')}
            size="large"
            sx={{ borderColor: '#081122', color: '#081122', px: 4, py: 1.5 }}
          >
            Export Inactive Employees
          </Button>
        </Box>
        <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
          The inactive export covers deactivated employees (leavers, notice period) who punched in at least once in the selected dates, so their worked days can be paid.
        </Typography>
      </Paper>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => !exporting && setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Download />
            <Typography variant="h6">
              {exportFilters.employee_status === 'inactive' ? 'Export Inactive Employees Attendance' : 'Export Attendance Data'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {exporting ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
              <CircularProgress size={48} />
              <Typography variant="body1" color="text.secondary">
                Generating attendance report... This may take a few moments.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Store</InputLabel>
                <Select
                  value={exportFilters.store_id}
                  label="Store"
                  onChange={(e) => setExportFilters({ ...exportFilters, store_id: e.target.value })}
                  disabled={exporting}
                >
                  <MenuItem value="all">All Stores</MenuItem>
                  {stores.map((store) => (
                    <MenuItem key={store.id} value={store.id}>
                      {store.name} ({store.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Start Date"
                type="date"
                value={exportFilters.start_date}
                onChange={(e) => setExportFilters({ ...exportFilters, start_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={exporting}
              />

              <TextField
                label="End Date"
                type="date"
                value={exportFilters.end_date}
                onChange={(e) => setExportFilters({ ...exportFilters, end_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={exporting}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setExportDialogOpen(false)}
            disabled={exporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            disabled={exporting || !exportFilters.start_date || !exportFilters.end_date}
            startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <Download />}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AttendanceExportPage;

